import {
  ChangeDetectionStrategy,
  Component,
  Input,
  signal,
  computed,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  untracked,
  inject,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { AddFormComponent } from '@shared/components/add-form/add-form.component';
import { BrandFormConfig } from '../../brand.config';
import { BrandService } from '../../services/brand.service';
import { Toast } from "primeng/toast";
import { MessageService } from 'primeng/api';
import { PermissionService } from '@app/core/services/permission.service';

@Component({
  selector: 'app-brand-form-wrapper',
  standalone: true,
  imports: [CommonModule, ButtonModule, AddFormComponent, TranslateModule, Toast],
  templateUrl: './brand-form-wrapper.component.html',
  styleUrls: ['./brand-form-wrapper.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BrandFormWrapperComponent implements OnInit, OnDestroy, OnChanges {
  @Input() config!: BrandFormConfig;
  @Input() editMode = false;
  @Input() viewMode = false;
  @Input() initialData: any = null;
  @ViewChild(AddFormComponent) addFormComponent?: AddFormComponent;

  // Form data management
  formData = signal<any>({});
  logoData = signal<any>(null);
  loading = signal(false);
  isImageChanged = signal(false); // Track if image has been uploaded/changed
  private skipCanDeactivate = false;

  // Computed property to combine all form data for initialData
  combinedInitialData = computed(() => {
    const baseData = {
      ...untracked(() => this.formData()),
      logo: this.logoData(),
    };

    return baseData;
  });

  private readonly permissionService = inject(PermissionService);

  // Permission flags - computed from PermissionService
  pim_approve = computed(() => this.permissionService.canApproveSpu());

  // Updated form configuration with permission-based field disabling
  updatedFormConfig = computed(() => {
    if (!this.config) return this.config;

    const updatedConfig = { ...this.config };
    updatedConfig.formFields = this.config.formFields.map((field) => {
      if (field.controlName === 'status') {
        // Disable status dropdown if user doesn't have P_PIM.APPROVE permission
        const updatedField = {
          ...field,
          disabled: !this.pim_approve(),
        };
        return updatedField;
      }
      return field;
    });

    return updatedConfig;
  });

  private _valueChangeTimeout: any;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly brandService: BrandService,
    private readonly translateService: TranslateService,
    private readonly messageService: MessageService

  ) {}

  ngOnInit(): void {
    // Initial setup - will be handled by ngOnChanges
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle initialData changes
    if (changes['initialData'] && this.editMode && this.initialData) {
      // Create a copy of the initial data
      const formDataCopy = { ...this.initialData };

      // Handle logo field - convert URL to FileUploadData structure
      if (this.initialData.logo && typeof this.initialData.logo === 'string') {
        // Create FileUploadData structure for existing logo without dummy file
        const logoData = {
          file: null, // No file for existing logo
          previewUrl: this.initialData.logo, // Use the URL as preview
          fileName: this.initialData.logo.split('/').pop() || 'existing-logo', // Extract filename from URL
          fileSize: 0, // We don't know the actual size
          isExisting: true, // Flag to indicate this is an existing logo
        };

        formDataCopy.logo = logoData;
        this.logoData.set(logoData);
        this.isImageChanged.set(false); // Logo hasn't changed yet
      }

      this.formData.set(formDataCopy);
    }
  }

  ngOnDestroy(): void {
    if (this._valueChangeTimeout) {
      clearTimeout(this._valueChangeTimeout);
    }
  }

  onFormValueChanges(value: any): void {
    // Use debouncing to prevent UI jumps from rapid value changes
    if (this._valueChangeTimeout) {
      clearTimeout(this._valueChangeTimeout);
    }

    this._valueChangeTimeout = setTimeout(() => {
      this._handleFormValueChanges(value);
    }, 100);
  }

  private _handleFormValueChanges(value: any): void {
    this.formData.set(value);

    // Only update logoData if it's provided and different from current
    if (value.logo && value.logo !== this.logoData()) {
      this.logoData.set(value.logo);
    }

    // Note: isImageChanged flag is now handled by file upload events
    // This method only handles form field changes (brandName, code, manufacturer, remark, etc.)
  }

  onFormAction(event: { action: string; data?: any }): void {
    if (event.action === 'save' && event.data) {
      // Prevent multiple API calls if already saving
      if (this.loading()) {
        return;
      }
      // Set loading state immediately to prevent race conditions
      this.loading.set(true);
      this.saveBrand(event.data);
    } else if (event.action === 'cancel') {
      // Get the current query parameters from the route
      const currentQueryParams = this.route.snapshot.queryParams;

      // Remove the 'id' parameter when navigating back to list page
      const { id, ...filterParams } = currentQueryParams;

      // Navigate back to the list page with preserved filter parameters
      this.router.navigate(['/goods/brand'], {
        queryParams: filterParams,
      });
    }
  }

  canDeactivate(): boolean {
    if (this.viewMode || this.skipCanDeactivate || this.loading()) {
      return true;
    }

    return !(this.addFormComponent?.form?.dirty ?? false);
  }

  // Handle logo removal from UI
  onLogoRemoved(): void {
    this.logoData.set(null);
    this.isImageChanged.set(true); // Logo has changed (removed)
  }

  // Handle logo file change from file upload component
  onLogoFileChange(event: { fieldName: string; fileData: any }): void {
    // Only handle logo field changes
    if (event.fieldName === 'logo') {
      if (event.fileData) {
        // New file uploaded or existing file modified
        this.logoData.set(event.fileData);
        this.isImageChanged.set(true); // Logo has changed
      } else {
        // File removed
        this.logoData.set(null);
        this.isImageChanged.set(true); // Logo has changed (removed)
      }
    }
  }

  private saveBrand(brandData: any): void {
    const spuId = this.route.snapshot.queryParams['id'];

    // Prepare the data for submission
    const submitData = { ...brandData };

    // Handle logo data based on isImageChanged flag
    if (this.isImageChanged()) {
      // Image has changed, check if it's a removal or upload
      if (this.logoData() === null) {
        // User removed the logo
        submitData.logo = null; // Set logo to null to indicate removal
        submitData.removeLogo = true; // Tell backend to remove the logo
        submitData.isImageChanged = true; // Pass flag to service
      } else {
        // User uploaded new logo
        submitData.removeLogo = false; // Not removing logo
        submitData.isImageChanged = true; // Pass flag to service
      }
    } else {
      // Image has not changed, remove logo field from payload
      delete submitData.logo; // Remove logo field completely
      submitData.removeLogo = false; // Don't remove logo, preserve existing one
      submitData.isImageChanged = false; // Pass flag to service
    }

    if (this.editMode && spuId) {
      // Update existing brand
      this.brandService.updateBrand(spuId, submitData).subscribe({
        next: (response) => {
          this.loading.set(false);
          this.skipCanDeactivate = true;
          this.addFormComponent?.form?.markAsPristine();

          // Emit success event through service
          this.brandService.emitBrandOperationResult({
            success: true,
            message: this.translateService.instant(
              'brand.messages.success.brand_updated'
            ),
            type: 'update',
          });

          // Navigate immediately after success with preserved filter parameters
          const currentQueryParams = this.route.snapshot.queryParams;
          const { id, ...filterParams } = currentQueryParams;
          this.router.navigate(['/goods/brand'], {
            queryParams: filterParams,
          });
        },
        error: (error) => {
          this.loading.set(false);
          this.messageService.add(this.brandService.handleBackendValidationError(error, 'update')); 
          
        },
      });
    } else {
      // Create new brand
      this.brandService.createBrand(submitData).subscribe({
        next: (response) => {
          this.loading.set(false);
          this.skipCanDeactivate = true;
          this.addFormComponent?.form?.markAsPristine();

          // Emit success event through service
          this.brandService.emitBrandOperationResult({
            success: true,
            message: this.translateService.instant(
              'brand.messages.success.brand_created'
            ),
            type: 'create',
          });

          // Navigate immediately after success with preserved query parameters
          const currentQueryParams = this.route.snapshot.queryParams;
          const { id, ...filterParams } = currentQueryParams;
          this.router.navigate(['/goods/brand'], {
            queryParams: filterParams,
          });
        },
        error: (error) => {
          this.loading.set(false);

          // Emit error event through service
          this.messageService.add(this.brandService.handleBackendValidationError(error, 'create')); 
        },
      });
    }
  }
}
